import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errMsg, fmt, ts } from "../lib/api";
import { Panel, PageHead, Spinner, Btn, Empty } from "../components/common";
import { Switch } from "../components/ui/switch";
import { SellHolding } from "../components/SellHolding";
import { ShortPanel, CoverButton } from "../components/ShortTrade";
import { PnlWeekly } from "../components/PnlWeekly";
import ChartPane from "../components/ChartPane";
import { useFetch } from "../lib/useFetch";

const SRC = { ai_suggested: "AI suggestion (edit if you like)", owner: "Set by you", dmi_trade: "DMI trade" };

function Levels({ w, onSaved }) {
  const init = () => ({ stop: w.stop ?? "", tp1: w.tp1 ?? "", tp2: w.tp2 ?? "", tp3: w.tp3 ?? "", auto_sell: !!w.auto_sell });
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(init()), [w.stop, w.tp1, w.tp2, w.tp3, w.auto_sell]); // eslint-disable-line react-hooks/exhaustive-deps
  const hits = w.tp_hits || [];
  const id = w.code.replace(/\./g, "-");
  const managed = w.levels_source === "dmi_trade";
  const save = async (body = f) => {
    setBusy(true);
    try { await api.post(`/holdings/watch/${w.code}`, { stop: Number(body.stop), tp1: Number(body.tp1), tp2: Number(body.tp2), tp3: Number(body.tp3), auto_sell: body.auto_sell }); toast.success(`${w.code} saved`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 text-xs">
      {(w.side === "short" ? [["stop", "SL (beli balik semua)", "text-red-700"], ["tp1", "TP1 (beli balik 1/3)"], ["tp2", "TP2 (beli balik 1/3)"], ["tp3", "TP3 (beli balik baki)"]]
        : [["stop", "SL (jual semua)", "text-red-700"], ["tp1", "TP1 (jual 1/3)"], ["tp2", "TP2 (jual 1/3)"], ["tp3", "TP3 (jual baki)"]]).map(([k, label, c]) => (
        <label key={k} className={`flex flex-col gap-1 ${c || "text-emerald-700"}`}>{label}{hits.includes(k) && <span className="text-[10px] font-bold">✓ dah capai</span>}
          <input data-testid={`watch-${k}-${id}`} disabled={managed} type="number" step="any" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mono w-24 rounded-sm border border-slate-300 px-2 py-1 text-slate-900" />
        </label>))}
      <label className="flex items-center gap-2 pb-1">
        <Switch data-testid={`watch-autosell-${id}`} disabled={managed || busy} checked={f.auto_sell} onCheckedChange={(v) => { const n = { ...f, auto_sell: v }; setF(n); save(n); }} />
        <span className={f.auto_sell ? "font-semibold text-red-700" : "text-slate-600"}>{f.auto_sell ? "Auto-sell ON" : "Auto-sell OFF (email only)"}</span>
      </label>
      {!managed && <Btn data-testid={`watch-save-${id}`} disabled={busy} onClick={() => save()}>Save</Btn>}
    </div>
  );
}

function Holding({ w, liveActive, reload }) {
  const [tf, setTf] = useState("1h");
  const id = w.code.replace(/\./g, "-");
  const pl = Number(w.pl_val || 0);
  return (
    <Panel testid={`watch-card-${id}`} className="mb-3" title={<span className="mono">{w.code} <span className={`rounded-sm px-1.5 text-[10px] font-bold ${w.side === "short" ? "bg-red-600 text-white" : "bg-blue-600 text-white"}`} data-testid={`watch-side-${id}`}>{w.side === "short" ? "SHORT" : "BELI"}</span> <span className="font-normal text-slate-500">{w.name}</span></span>}
      right={w.side === "short" ? (liveActive && <CoverButton w={w} onDone={reload} />) : <SellHolding p={{ code: w.code, qty: w.qty, stock_name: w.name, nominal_price: w.price, currency: w.currency }} liveActive={liveActive} />}>
      <div className="mb-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
        <div>Qty <div className="mono font-semibold" data-testid={`watch-qty-${id}`}>{w.qty}</div></div>
        <div>Cost <div className="mono font-semibold">{w.cost ?? "—"}</div></div>
        <div>Price <div className="mono font-semibold" data-testid={`watch-price-${id}`}>{w.price} {w.currency}</div></div>
        <div>P/L <div className={`mono font-semibold ${pl > 0 ? "text-emerald-600" : pl < 0 ? "text-red-600" : ""}`} data-testid={`watch-pl-${id}`}>{fmt(pl, 2)} {w.currency}</div></div>
        <div>AI signal <div className="mono font-semibold" data-testid={`watch-signal-${id}`}>{w.last_signal || "—"}</div></div>
      </div>
      <p className="mb-2 text-[11px] text-slate-500" data-testid={`watch-source-${id}`}>Levels: {SRC[w.levels_source] || w.levels_source}{w.basis ? ` · ${w.basis}` : ""}{w.checked_at ? ` · checked ${ts(w.checked_at)}` : ""}</p>
      {w.sell_error && <p className="mb-2 text-[11px] text-red-600" data-testid={`watch-sell-error-${id}`}>Last auto-sell failed: {w.sell_error}</p>}
      {w.sell_pending && <p className="mb-2 text-[11px] text-amber-700" data-testid={`watch-sell-pending-${id}`}>Auto-sell sent ({w.sell_reason?.replace("_", " ")}) - waiting for Moomoo.</p>}
      <Levels w={w} onSaved={reload} />
      {w.chartable ? (
        <div className="mt-3" data-testid={`watch-chart-${id}`}>
          <ChartPane id={`watch-${id}`} symbol={w.symbol} tf={tf} onTf={setTf} showPatterns={false} height={280} hideTrade={w.side === "short"} />
        </div>
      ) : <p className="mt-3 text-[11px] text-slate-500" data-testid={`watch-nochart-${id}`}>No chart: {w.symbol} is not in the DMI stock list. Monitoring and auto-sell still work using the Moomoo price.</p>}
    </Panel>
  );
}

function Pending({ rows, reload }) {
  if (!rows?.length) return null;
  const cancel = async (id) => { try { await api.post(`/orders/${id}/cancel`); toast.success("Order dibatalkan"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Order menunggu (belum dibeli)" testid="watch-pending" className="mb-3">
      <p className="mb-2 text-[11px] text-slate-600">WORKING = order sudah dihantar ke Moomoo tetapi <b>belum dibeli</b>. Ia jadi FILLED (dah beli) bila harga pasaran sampai harga had anda, dan saham akan muncul di bawah. Tekan Batal jika tidak mahu lagi. Order had dari DMI dikemas kini ke harga semasa setiap minit (maksimum 3 kali), kemudian dibatalkan jika masih belum dibeli.</p>
      {rows.map((o) => (
        <div key={o.id} data-testid={`pending-row-${o.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 py-1.5 text-xs">
          <span className="mono font-semibold">{o.side?.toUpperCase()} {o.symbol}</span><span className="mono">{o.filled_qty || 0}/{o.qty}</span>
          <span className="mono">{o.type === "market" ? "market" : `@ ${o.limit_price}`}</span>
          <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`pending-status-${o.id}`}>{o.status === "PARTIALLY_FILLED" ? "SEBAHAGIAN DIBELI" : "MENUNGGU"}</span>
          <span className="text-slate-500">{ts(o.created_at)}</span>
          <Btn variant="danger" className="ml-auto" onClick={() => cancel(o.id)} data-testid={`pending-cancel-${o.id}`}>Batal</Btn>
        </div>))}
    </Panel>
  );
}

function BrokerOrders() {
  const { data } = useFetch("/live/broker-orders", 60000);
  if (!data) return null;
  const done = data.orders.filter((o) => !["SUBMITTED", "FILLED_PART", "SUBMITTING", "WAITING_SUBMIT"].includes(o.status)).slice(0, 30);
  return (
    <Panel title="Order selesai di Moomoo (harga sebenar)" testid="broker-orders" className="mb-3">
      {!!data.exits?.length && (
        <div className="mb-3" data-testid="watch-exits">
          <p className="mb-1 text-[11px] font-semibold text-slate-700">Jualan / beli balik oleh DMI</p>
          {data.exits.map((x) => (
            <div key={x.order_id} data-testid={`exit-row-${x.order_id}`} className="flex flex-wrap gap-x-3 border-t border-slate-100 py-1 text-xs">
              <span className="mono font-semibold">{x.side === "buy" ? "BELI BALIK" : "JUAL"} {x.code}</span><span className="mono">{x.fill_qty ?? x.qty}</span>
              <span className="mono text-slate-500">anggaran {x.est_price}</span>
              <span className="mono font-semibold" data-testid={`exit-fill-${x.order_id}`}>{x.fill_price == null ? "menunggu harga sebenar…" : x.fill_price === 0 ? "tidak dilaksana" : `sebenar ${x.fill_price}`}</span>
              <span className="text-slate-500">{x.reason?.replace("_", " ")} · {ts(x.ts)}</span>
            </div>))}
        </div>)}
      {!!data.errors?.length && <p className="mb-1 text-[11px] text-red-600" data-testid="broker-orders-error">{data.errors.join(" · ")}</p>}
      {!done.length ? <p className="text-xs text-slate-500" data-testid="broker-orders-empty">Tiada order selesai dalam 90 hari.</p> : done.map((o) => (
        <div key={o.order_id} data-testid={`broker-order-${o.order_id}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-slate-100 py-1 text-xs">
          <span className={`mono font-semibold ${o.side === "BUY" ? "text-blue-700" : "text-red-700"}`}>{o.side === "BUY" ? "BELI" : "JUAL"} {o.code}</span>
          <span className="mono">{Number(o.filled_qty || 0)}/{o.qty}</span>
          <span className="mono">{Number(o.avg_fill_price) > 0 ? `@ ${o.avg_fill_price} ${o.currency}` : o.price ? `had ${o.price}` : "market"}</span>
          <span className="rounded-sm bg-slate-100 px-1.5 text-[10px] font-bold text-slate-700">{o.status_ms}</span>
          <span className="text-slate-500">{o.source} · {ts(o.updated_at)}</span>
        </div>))}
    </Panel>
  );
}

export default function MyHoldings() {
  const { data, reload, setData } = useFetch("/holdings/watch", 30000);
  const live = useFetch("/accounts/live-summary", 60000).data;
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.get("/holdings/watch", { params: { refresh: true } }); setData(r.data); if (r.data.error) toast.error(r.data.error); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="my-holdings-page">
      <PageHead title="Saham Saya (LIVE)" sub={`Every share in your Moomoo LIVE account is watched each cycle. Chart shows BELI (your buy price), SL, TP1, TP2, TP3. Email when price is within ${data?.near_pct ?? 1}% of SL/next TP or the AI signal turns SELL. Auto-sell (real market orders, market hours): all at SL, 1/3 at TP1 (then SL moves to your buy price), 1/3 at TP2, the rest at TP3 (Emergency Stop does not block sells).`}
        right={<Btn variant="ghost" onClick={refresh} disabled={busy} data-testid="watch-refresh"><RefreshCw className={`mr-1 inline h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />Refresh from Moomoo</Btn>} />
      <Pending rows={data?.pending_orders} reload={reload} />
      <ShortPanel />
      {!data ? <Spinner /> : !data.holdings.length ? <Empty><span data-testid="watch-empty">No shares in your Moomoo LIVE account yet. Bought shares appear here automatically once filled.</span></Empty>
        : data.holdings.map((w) => <Holding key={w.code} w={w} liveActive={live?.live_active} reload={reload} />)}
      {!!data?.recently_closed?.length && (
        <Panel title="Recently sold / closed" testid="watch-closed">
          <ul className="text-xs text-slate-600">{data.recently_closed.map((w) => <li key={w.code} className="mono">{w.code} · closed {ts(w.closed_at)}{w.sell_reason ? ` · ${w.sell_reason.replace("_", " ")}` : ""}</li>)}</ul>
        </Panel>
      )}
      <BrokerOrders />
      <PnlWeekly />
    </div>
  );
}
